const browser = typeof window === 'undefined' ? {} : window

const defaultApi = {
  post: async (...args) => (await import('axios')).default.post(...args),
  delete: async (...args) => (await import('axios')).default.delete(...args),
}

const firebaseConfig = () => ({
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
})

const getFirebaseMessaging = async () => {
  const [{ initializeApp, getApps }, { getMessaging }] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ])
  const app = getApps()[0] || initializeApp(firebaseConfig())
  return getMessaging(app)
}

const defaultGetToken = async ({ registration }) => {
  const { getToken } = await import('firebase/messaging')
  return getToken(await getFirebaseMessaging(), {
    vapidKey: process.env.VUE_APP_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: registration,
  })
}

const defaultDeleteToken = async () => {
  const { deleteToken } = await import('firebase/messaging')
  return deleteToken(await getFirebaseMessaging())
}

export const createPushNotificationService = ({
  window: target = browser,
  api = defaultApi,
  getToken = defaultGetToken,
  deleteToken = defaultDeleteToken,
  apiBaseUrl = process.env.VUE_APP_API_URL || '',
} = {}) => {
  const endpoint = `${apiBaseUrl.replace(/\/$/, '')}/push-device`
  const supported = () => Boolean(target.Notification && target.navigator?.serviceWorker)
  const shouldPromptForPush = ({ receiveNotifications = false } = {}) =>
    Boolean(receiveNotifications && supported() && target.Notification.permission === 'default')

  const registerGrantedToken = async () => {
    const registration = await target.navigator.serviceWorker.ready
    const token = await getToken({ registration })
    if (!token) {
      return { enabled: false, reason: 'token-unavailable' }
    }
    await api.post(endpoint, { token })
    return { enabled: true }
  }

  const enablePushNotifications = async () => {
    if (!supported()) {
      return { enabled: false, reason: 'unsupported' }
    }
    const permission = target.Notification.permission === 'granted'
      ? 'granted'
      : await target.Notification.requestPermission()
    if (permission !== 'granted') {
      return { enabled: false, reason: 'denied' }
    }
    return registerGrantedToken()
  }

  const releasePushToken = async () => {
    if (!supported() || target.Notification.permission !== 'granted') {
      return { enabled: false }
    }
    const registration = await target.navigator.serviceWorker.getRegistration()
    if (!registration) {
      return { enabled: false }
    }
    try {
      const token = await getToken({ registration })
      if (token) {
        await api.delete(endpoint, { data: { token } })
      }
    } finally {
      await deleteToken({ registration })
    }
    return { enabled: false }
  }

  const syncPushToken = async ({ receiveNotifications = false } = {}) => {
    if (!supported()) {
      return { enabled: false, reason: 'unsupported' }
    }
    if (target.Notification.permission !== 'granted') {
      return { enabled: false, reason: 'not-granted' }
    }
    if (!receiveNotifications) {
      return releasePushToken()
    }
    return registerGrantedToken()
  }

  const disablePushNotifications = async () => {
    await api.delete(endpoint)
    return { enabled: false }
  }

  return { enablePushNotifications, disablePushNotifications, syncPushToken, releasePushToken, shouldPromptForPush }
}

const service = createPushNotificationService()
export const enablePushNotifications = service.enablePushNotifications
export const disablePushNotifications = service.disablePushNotifications
export const syncPushToken = service.syncPushToken
export const releasePushToken = service.releasePushToken
export const shouldPromptForPush = service.shouldPromptForPush
